import type { AddItemsLine } from "@/cart/addItems";
import type { AddItemContext, CartStateGetSet } from "@/cart/types";
import GRAPHQL_CLIENT from "@/graphql/client";
import type { Cart } from "@/graphql/storefront/generated";
import {
  isLegacyReferralReward,
  isReferralRewardBagPass,
} from "@/utils/ShopifyHelpers";
import { isMaticFamilyVariantId } from "@/utils/maticFamily";

/**
 * Makes the cart hold exactly `items` for the variants a configurator owns:
 * every line of `ownedVariantIds` goes (with the referral reward lines a
 * removed Matic carried), then `items` are added as one batch, so the
 * add's own referral sync starts from a cart without them. Lines of any other
 * variant are left alone. Two mutations, not one: should the add fail, the
 * owned lines are already gone, and the error reaches the caller.
 */
export const replaceItems = ({ get, set }: CartStateGetSet) => {
  return async (
    ownedVariantIds: ReadonlySet<string>,
    items: AddItemsLine[],
    context?: AddItemContext,
    options?: { cartAttributes?: Record<string, string> },
  ) => {
    const { cartId, cart } = get();
    const lines = cart?.lines.edges.map(({ node }) => node) ?? [];
    const removesMatic = lines.some(
      (node) =>
        ownedVariantIds.has(node.merchandise.id) &&
        isMaticFamilyVariantId(node.merchandise.id),
    );
    const lineIds = lines
      .filter(
        (node) =>
          ownedVariantIds.has(node.merchandise.id) ||
          (removesMatic &&
            (isLegacyReferralReward(node) || isReferralRewardBagPass(node))),
      )
      .map((node) => node.id);

    if (cartId !== undefined && lineIds.length > 0) {
      const { cartLinesRemove } = await GRAPHQL_CLIENT.RemoveFromCart({
        cartId,
        lineIds,
      });
      const nextCart = cartLinesRemove?.cart as Cart | undefined;
      if (nextCart === undefined) {
        throw new Error("replaceItems: removing the owned lines failed");
      }
      set({ cartId: nextCart.id, cart: nextCart });
    }

    return get().addItems(items, context, options);
  };
};
